import { useState, useMemo, useContext, createContext } from 'react'
import Dialog from '@mui/material/Dialog'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import PlanningRecordReviewPanel from '../../features/planningRecords/PlanningRecordReviewPanel'
import { useOverlayPageNavigateBack } from '../../../hooks/navigation/usePersistedBackgroundLocation'

const PlanningRecordDialogContext = createContext<{
  openPlanningRecordDialog: (args: { planningRecordId: string }) => void
  closePlanningRecordDialog: () => void
} | null>(null)
const usePlanningRecordDialog = ({ planningRecordId }: { planningRecordId: string }) => {
  const context = useContext(PlanningRecordDialogContext)
  if (!context) {
    throw new Error('no PlanningRecordDialogProvider found when calling usePlanningRecordDialog()')
  }
  const { openPlanningRecordDialog, closePlanningRecordDialog } = context
  const wrappedOpenPlanningRecordDialog = useCallbackDebounced(() => {
    openPlanningRecordDialog({ planningRecordId })
  }, [openPlanningRecordDialog, planningRecordId])
  return {
    openPlanningRecordDialog: wrappedOpenPlanningRecordDialog,
    closePlanningRecordDialog,
  }
}
const PlanningRecordDialogProvider = ({ children }) => {
  const overlayPageNavigateBack = useOverlayPageNavigateBack()
  const [open, setOpen] = useState(false)
  const [planningRecordId, setPlanningRecordId] = useState<string>()
  const openPlanningRecordDialog = useCallbackDebounced(
    ({ planningRecordId }: { planningRecordId: string }) => {
      setPlanningRecordId(planningRecordId)
      setOpen(true)
    },
    [],
  )
  const closePlanningRecordDialog = useCallbackDebounced(() => {
    setOpen(false)
  }, [])
  const onClose = useCallbackDebounced(() => {
    overlayPageNavigateBack()
  }, [overlayPageNavigateBack])
  const onExited = useCallbackDebounced(() => {
    setPlanningRecordId(undefined)
  }, [])
  const value = useMemo(
    () => ({
      openPlanningRecordDialog,
      closePlanningRecordDialog,
    }),
    [closePlanningRecordDialog, openPlanningRecordDialog],
  )
  return (
    <PlanningRecordDialogContext.Provider value={value}>
      {children}
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        sx={{
          zIndex: 20,
        }}
        disableEscapeKeyDown
        slotProps={{
          transition: {
            onExited,
            mountOnEnter: true,
            unmountOnExit: true,
          },
        }}
      >
        {planningRecordId && (
          <PlanningRecordReviewPanel planningRecordId={planningRecordId} onClose={onClose} />
        )}
      </Dialog>
    </PlanningRecordDialogContext.Provider>
  )
}

export { usePlanningRecordDialog, PlanningRecordDialogProvider }
export default PlanningRecordDialogProvider
